import React from 'react';
import { Table } from '@/components';
import { loadingTableHandler } from '@/utils/handler/loadingTableHandler';
import { formatDealerAmount } from '@/utils/currency';
import { Card, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { format } from 'date-fns';
import { IDealersCurrency } from '@/types';
import { ICreditControlRecord } from '../types';

interface CreditHistoryTableProps {
  data: ICreditControlRecord[];
  loading: boolean;
  dealersCurrency?: IDealersCurrency;
  exchangeRate?: number;
}

export const CreditHistoryTable: React.FC<CreditHistoryTableProps> = ({
  data,
  loading,
  dealersCurrency,
  exchangeRate,
}) => {
  const formatAmount = (value: number) =>
    formatDealerAmount(value, dealersCurrency, exchangeRate ?? dealersCurrency?.dogma_exchangerate ?? 1);

  const columns: ColumnsType<ICreditControlRecord> = [
    {
      title: 'Name',
      dataIndex: 'dogma_name',
      key: 'dogma_name',
      render: (name: string) => (
        <span className="font-medium text-gray-900">{name}</span>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'dogma_type',
      key: 'dogma_type',
      render: (_: any, record: ICreditControlRecord) => {
        const typeLabel = record['dogma_type@OData.Community.Display.V1.FormattedValue'];
        return typeLabel ? (
          <Tag color="blue">{typeLabel}</Tag>
        ) : (
          <span className="text-gray-400">N/A</span>
        );
      },
    },
    {
      title: 'Credit Limit',
      dataIndex: 'dogma_creditlimit',
      key: 'dogma_creditlimit',
      align: 'right' as const,
      render: (value: number | null) => {
        if (value === null) return <span className="text-gray-400">N/A</span>;
        return (
          <span className="font-semibold text-gray-900">
            {formatAmount(value)}
          </span>
        );
      },
    },
    {
      title: 'Available Credit',
      dataIndex: 'dogma_availablecredit',
      key: 'dogma_availablecredit',
      align: 'right' as const,
      render: (value: number | null) => {
        if (value === null) return <span className="text-gray-400">N/A</span>;
        const color = value > 0 ? 'text-green-600' : 'text-red-600';
        return (
          <span className={`font-semibold ${color}`}>
            {formatAmount(value)}
          </span>
        );
      },
    },
    {
      title: 'Created',
      dataIndex: 'createdon',
      key: 'createdon',
      render: (date: string) => (
        <span>{format(new Date(date), 'dd/MM/yyyy HH:mm')}</span>
      ),
    },
  ];

  return (
    <Card rootClassName='[&_.ant-card-body]:p-0 [&_.pagination]:px-4 [&_.pagination]:pb-3' className='p-0' title={<span className="text-lg font-semibold">Credit History</span>}>
      {loadingTableHandler(
        loading,
        <Table<ICreditControlRecord>
          columns={columns}
          dataSource={data}
          rowKey="dogma_creditcontrolid"
          isLoading={loading}
        />
      )}
    </Card>
  );
};

export default CreditHistoryTable;
